import { z } from "zod";
import { AgentId, ConversationId, Locale, NodeId, Ok, PageQuery, ProjectId, TaskId, TemplateId, TenantId, Timestamp, page } from "./common.js";
import { Actor, AgentPlacement, Conversation, ConversationKind, Message, Part, Template } from "./entities.js";
import {
  Asset,
  AssetId,
  AssetKind,
  AssetVersion,
  Currency,
  Device,
  HumanQuestion,
  Listing,
  ListingId,
  ListingReview,
  Payment,
  TenantBudget,
  Mail,
  MediaJob,
  Meeting,
  MeetingId,
  Notification,
  NotificationPrefs,
  PullRequest,
  Repo,
  RepoId,
  Rental,
  Task,
  TaskStatus,
  UsageKind,
  UsageRecord,
  Wallet,
} from "./collab.js";
import { r, type RouteDef } from "./route-def.js";

/**
 * P2–P6 路由（doc/api_v1.md §1.3、§1.7–§1.13）。每条都标 phase，控制面按阶段开放。
 */

const PidParams = z.object({ pid: ProjectId });
const TidParams = z.object({ tid: TenantId });
const CidParams = z.object({ cid: ConversationId });
const TkParams = z.object({ tkid: TaskId });
const PrParams = z.object({ rid: RepoId, num: z.coerce.number().int().positive() });
const MidParams = z.object({ mid: MeetingId });
const AsParams = z.object({ asid: AssetId });

export const NodeInfo = z.object({
  id: NodeId,
  hostname: z.string(),
  status: z.enum(["online", "unreachable", "draining", "revoked"]),
  load: z.object({ cpu: z.number(), memUsedMb: z.number(), memTotalMb: z.number(), diskFreeMb: z.number() }).nullable(),
  agents: z.number().int(),
  lastSeenAt: Timestamp.nullable(),
  runtimes: z.array(z.string()),
});

export const collabRoutes = {
  // ───────── P2：模板管理 ─────────
  templateCreate: r({
    phase: 2, method: "POST", path: "/tenants/:tid/templates", summary: "自定义身份模板", tags: ["template"], auth: "user", idempotent: true,
    permission: "project.write", params: TidParams, body: Template.omit({ id: true, tenantId: true, version: true }), response: Template,
  }),
  templatePatch: r({
    phase: 2, method: "PATCH", path: "/templates/:tplid", summary: "修改模板（版本 +1；已有 agent 需 reload 生效）", tags: ["template"], auth: "user",
    permission: "project.write", params: z.object({ tplid: TemplateId }), body: Template.omit({ id: true, tenantId: true, version: true }).partial(), response: Template,
  }),
  agentReload: r({
    phase: 2, method: "POST", path: "/agents/:aid/reload", summary: "按最新模板重新配置 agent", tags: ["agent"], auth: "user",
    permission: "agent.control", params: z.object({ aid: AgentId }), response: Ok,
  }),

  // ───────── P2：仓库 / PR ─────────
  repoList: r({ phase: 2, method: "GET", path: "/projects/:pid/repos", summary: "项目仓库", tags: ["git"], auth: "user", permission: "project.read", params: PidParams, response: z.object({ items: z.array(Repo) }) }),
  repoCreate: r({
    phase: 2, method: "POST", path: "/projects/:pid/repos", summary: "创建或导入仓库", tags: ["git"], auth: "user", idempotent: true, permission: "project.write",
    params: PidParams, body: z.object({ name: Repo.shape.name, importUrl: z.url().optional() }), response: Repo, errors: ["common.conflict", "git.unavailable"],
  }),
  pullList: r({
    phase: 2, method: "GET", path: "/projects/:pid/pulls", summary: "PR 列表", tags: ["git"], auth: "user", permission: "project.read",
    params: PidParams, query: PageQuery.extend({ state: z.enum(["open", "merged", "closed", "all"]).default("open") }), response: page(PullRequest),
  }),
  pullGet: r({ phase: 2, method: "GET", path: "/repos/:rid/pulls/:num", summary: "PR 详情", tags: ["git"], auth: "user", permission: "project.read", params: PrParams, response: PullRequest }),
  pullDiff: r({
    phase: 2, method: "GET", path: "/repos/:rid/pulls/:num/diff", summary: "PR diff（按文件）", tags: ["git"], auth: "user", permission: "project.read", params: PrParams,
    query: z.object({ file: z.string().optional() }),
    response: z.object({ files: z.array(z.object({ path: z.string(), status: z.enum(["added", "modified", "deleted", "renamed"]), additions: z.number(), deletions: z.number(), patch: z.string().nullable() })) }),
  }),
  pullReview: r({
    phase: 2, method: "POST", path: "/repos/:rid/pulls/:num/review", summary: "评审", tags: ["git"], auth: "user", permission: "code.review", params: PrParams,
    body: z.object({ event: z.enum(["approve", "request_changes", "comment"]), body: z.string().max(20000).default("") }), response: PullRequest,
  }),
  pullMerge: r({
    phase: 2, method: "POST", path: "/repos/:rid/pulls/:num/merge", summary: "合并", tags: ["git"], auth: "user", permission: "code.merge", params: PrParams,
    body: z.object({ method: z.enum(["merge", "squash", "rebase"]).default("squash") }), response: PullRequest, errors: ["git.not_mergeable"],
  }),

  // ───────── P2：任务 ─────────
  taskList: r({
    phase: 2, method: "GET", path: "/projects/:pid/tasks", summary: "任务（树或看板）", tags: ["task"], auth: "user", permission: "project.read", params: PidParams,
    query: z.object({ assignee: AgentId.optional(), status: TaskStatus.optional(), root: TaskId.optional() }), response: z.object({ items: z.array(Task) }),
  }),
  taskCreate: r({
    phase: 2, method: "POST", path: "/projects/:pid/tasks", summary: "人创建任务", tags: ["task"], auth: "user", idempotent: true, permission: "task.write", params: PidParams,
    body: z.object({ title: z.string().min(1).max(200), spec: z.string().max(20000).default(""), parentId: TaskId.optional(), assignee: AgentId.optional() }), response: Task,
  }),
  taskGet: r({ phase: 2, method: "GET", path: "/tasks/:tkid", summary: "任务详情", tags: ["task"], auth: "user", permission: "project.read", params: TkParams, response: Task }),
  taskAction: r({
    phase: 2, method: "POST", path: "/tasks/:tkid/actions/:action", summary: "分派 / 验收 / 返工 / 取消", tags: ["task"], auth: "user", permission: "task.write",
    params: TkParams.extend({ action: z.enum(["assign", "confirm", "rework", "cancel"]) }),
    body: z.object({ assignee: AgentId.optional(), feedback: z.string().optional() }), response: Task, errors: ["task.invalid_transition"],
  }),

  // ───────── P2：邮件（人只读 + 可给 agent 发邮件） ─────────
  mailbox: r({
    phase: 2, method: "GET", path: "/agents/:aid/mailbox", summary: "agent 邮箱（只读）", tags: ["mail"], auth: "user", permission: "agent.read",
    params: z.object({ aid: AgentId }), query: PageQuery.extend({ box: z.enum(["inbox", "sent"]).default("inbox") }), response: page(Mail),
  }),
  mailSend: r({
    phase: 2, method: "POST", path: "/projects/:pid/mails", summary: "人给 agent 发邮件", tags: ["mail"], auth: "user", idempotent: true, permission: "conversation.write",
    params: PidParams,
    // threadId：回复已有邮件线程（必须是本项目的 mail_thread）；缺省新开线程
    body: z.object({ to: z.array(AgentId).min(1), subject: z.string().max(200), body: z.string(), priority: Mail.shape.priority.default("normal"), threadId: ConversationId.optional() }),
    response: Mail,
  }),

  // ───────── P2：模板可选的模型 ─────────
  modelList: r({
    phase: 2, method: "GET", path: "/models", summary: "模板可选的模型（控制面配置 TEMPLATE_MODELS）", tags: ["template"], auth: "user",
    response: z.object({ items: z.array(z.object({ id: z.string(), default: z.boolean() })) }),
  }),

  // ───────── P2：向人提问（ask_human） ─────────
  questionList: r({
    phase: 2, method: "GET", path: "/projects/:pid/questions", summary: "agent 向人提的问题", tags: ["human"], auth: "user", permission: "project.read",
    params: PidParams, query: z.object({ status: z.enum(["open", "answered", "expired"]).optional() }), response: z.object({ items: z.array(HumanQuestion) }),
  }),
  questionAnswer: r({
    phase: 2, method: "POST", path: "/questions/:qid/answer", summary: "回答（唤醒 agent）", tags: ["human"], auth: "user", permission: "agent.control",
    // qid 形如 qst_<ULID>（QuestionId）；这里仍收 string，兼容旧客户端 / mock 的 id
    params: z.object({ qid: z.string() }), body: z.object({ answer: z.string().min(1) }), response: HumanQuestion, errors: ["question.already_answered"],
  }),

  // ───────── P3：聊天室 ─────────
  conversationCreate: r({
    phase: 3, method: "POST", path: "/projects/:pid/conversations", summary: "建聊天室 / 私聊", tags: ["conversation"], auth: "user", idempotent: true,
    permission: "conversation.write", params: PidParams,
    body: z.object({ kind: ConversationKind.extract(["room", "dm"]), title: z.string().max(100).optional(), members: z.array(Actor).min(1), hopLimit: z.number().int().min(1).max(20).optional(), budgetMicroUsd: z.number().int().nonnegative().nullable().optional() }),
    response: Conversation,
  }),
  conversationGet: r({ phase: 3, method: "GET", path: "/conversations/:cid", summary: "会话详情", tags: ["conversation"], auth: "user", permission: "project.read", params: CidParams, response: Conversation }),
  conversationPatch: r({
    phase: 3, method: "PATCH", path: "/conversations/:cid", summary: "改标题 / 护栏", tags: ["conversation"], auth: "user", permission: "conversation.write", params: CidParams,
    body: z.object({ title: z.string().max(100), hopLimit: z.number().int().min(1).max(20), budgetMicroUsd: z.number().int().nonnegative().nullable(), muted: z.boolean() }).partial(), response: Conversation,
  }),
  conversationMembers: r({
    phase: 3, method: "POST", path: "/conversations/:cid/members", summary: "增删成员", tags: ["conversation"], auth: "user", permission: "conversation.write", params: CidParams,
    body: z.object({ add: z.array(Actor).default([]), remove: z.array(Actor).default([]) }), response: Conversation,
  }),
  messagePost: r({
    phase: 3, method: "POST", path: "/conversations/:cid/messages", summary: "发消息（@ 唤醒）", tags: ["conversation"], auth: "user", idempotent: true, permission: "conversation.write",
    params: CidParams, body: z.object({ parts: z.array(Part).min(1), mentions: z.array(AgentId).default([]), replyTo: z.string().optional() }), response: Message,
    errors: ["conversation.paused"],
  }),
  conversationGuard: r({
    phase: 3, method: "POST", path: "/conversations/:cid/:op", summary: "暂停 / 恢复（护栏暂停后由人恢复）", tags: ["conversation"], auth: "user", permission: "conversation.write",
    params: CidParams.extend({ op: z.enum(["pause", "resume"]) }), response: Conversation,
  }),

  // ───────── P3：通知 / 设备（Web Push） ─────────
  notificationList: r({ phase: 3, method: "GET", path: "/notifications", summary: "通知", tags: ["notification"], auth: "user", query: PageQuery, response: page(Notification) }),
  notificationRead: r({
    phase: 3, method: "POST", path: "/notifications/read", summary: "标记已读", tags: ["notification"], auth: "user",
    body: z.union([z.object({ ids: z.array(z.string()).min(1) }), z.object({ all: z.literal(true) })]), response: Ok,
  }),
  deviceRegister: r({
    phase: 3, method: "POST", path: "/devices", summary: "注册推送设备", tags: ["notification"], auth: "user", idempotent: true,
    body: z.object({ platform: z.enum(["webpush", "apns", "fcm"]), token: z.string(), locale: Locale }), response: z.object({ id: z.string() }),
  }),
  deviceDelete: r({ phase: 3, method: "DELETE", path: "/devices/:did", summary: "注销设备", tags: ["notification"], auth: "user", params: z.object({ did: z.string() }), response: Ok }),
  notificationPrefs: r({ phase: 3, method: "PUT", path: "/me/notification-prefs", summary: "通知偏好（合并更新，返回完整偏好）", tags: ["notification"], auth: "user", body: NotificationPrefs, response: NotificationPrefs }),
  notificationPrefsGet: r({ phase: 3, method: "GET", path: "/me/notification-prefs", summary: "读取通知偏好（含默认值的完整偏好）", tags: ["notification"], auth: "user", response: NotificationPrefs }),
  notificationUnreadCount: r({ phase: 3, method: "GET", path: "/notifications/unread-count", summary: "未读通知数", tags: ["notification"], auth: "user", response: z.object({ count: z.number().int().nonnegative() }) }),
  deviceList: r({ phase: 3, method: "GET", path: "/devices", summary: "我的推送设备", tags: ["notification"], auth: "user", response: z.object({ items: z.array(Device) }) }),
  webPushKey: r({ phase: 3, method: "GET", path: "/push/vapid-key", summary: "Web Push 公钥", tags: ["notification"], auth: "user", response: z.object({ publicKey: z.string() }) }),

  // ───────── P4：资产 ─────────
  assetList: r({
    phase: 4, method: "GET", path: "/projects/:pid/assets", summary: "资产库", tags: ["asset"], auth: "user", permission: "project.read", params: PidParams,
    query: PageQuery.extend({ kind: AssetKind.optional(), tag: z.string().optional(), q: z.string().optional() }), response: page(Asset),
  }),
  assetGet: r({ phase: 4, method: "GET", path: "/assets/:asid", summary: "资产详情（含版本）", tags: ["asset"], auth: "user", permission: "project.read", params: AsParams, response: Asset.required({ versions: true }) }),
  assetDownload: r({
    phase: 4, method: "GET", path: "/assets/:asid/download", summary: "预签名下载地址", tags: ["asset"], auth: "user", permission: "project.read", params: AsParams,
    query: z.object({ version: z.coerce.number().int().positive().optional(), variant: z.enum(["original", "thumb", "preview"]).default("original") }),
    response: z.object({ url: z.url(), expiresAt: Timestamp, mediaType: z.string() }),
  }),
  assetFeedback: r({
    phase: 4, method: "POST", path: "/assets/:asid/feedback", summary: "打分评论（转发给美术 agent）", tags: ["asset"], auth: "user", permission: "asset.write", params: AsParams,
    body: z.object({ version: z.number().int().positive().optional(), rating: z.number().int().min(1).max(5).optional(), comment: z.string().max(5000).optional(), notify: z.array(AgentId).default([]) }),
    response: Asset,
  }),
  assetUpload: r({
    phase: 4, method: "POST", path: "/projects/:pid/assets", summary: "人上传参考资产（返回预签名上传地址）", tags: ["asset"], auth: "user", idempotent: true, permission: "asset.write", params: PidParams,
    body: z.object({ kind: AssetKind, name: z.string().max(200), mediaType: z.string(), size: z.number().int().positive(), tags: z.array(z.string()).default([]) }),
    response: z.object({ asset: Asset, uploadUrl: z.url(), version: AssetVersion.shape.version }),
  }),
  mediaJobList: r({
    phase: 4, method: "GET", path: "/projects/:pid/media-jobs", summary: "生成任务", tags: ["asset"], auth: "user", permission: "project.read", params: PidParams,
    query: PageQuery, response: page(MediaJob),
  }),

  // ───────── P5：会议 ─────────
  meetingCreate: r({
    phase: 5, method: "POST", path: "/projects/:pid/meetings", summary: "发起会议", tags: ["meeting"], auth: "user", idempotent: true, permission: "conversation.write", params: PidParams,
    body: z.object({ title: z.string().max(200), agenda: z.array(z.string()).min(1), moderator: Actor, participants: z.array(AgentId).min(1), maxRounds: z.number().int().min(1).max(20).default(3), budgetMicroUsd: z.number().int().nonnegative().nullable().optional() }),
    response: Meeting,
  }),
  meetingList: r({ phase: 5, method: "GET", path: "/projects/:pid/meetings", summary: "会议列表", tags: ["meeting"], auth: "user", permission: "project.read", params: PidParams, query: PageQuery, response: page(Meeting) }),
  meetingGet: r({ phase: 5, method: "GET", path: "/meetings/:mid", summary: "会议详情", tags: ["meeting"], auth: "user", permission: "project.read", params: MidParams, response: Meeting }),
  meetingAction: r({
    phase: 5, method: "POST", path: "/meetings/:mid/actions/:action", summary: "开始 / 下一轮 / 结束", tags: ["meeting"], auth: "user", permission: "conversation.write",
    params: MidParams.extend({ action: z.enum(["start", "next-round", "close"]) }), response: Meeting, errors: ["meeting.invalid_state"],
  }),
  meetingMinutes: r({
    phase: 5, method: "PATCH", path: "/meetings/:mid/minutes", summary: "编辑纪要（结束前后都可以；行动项在结束时转成任务，这里不改）", tags: ["meeting"], auth: "user",
    permission: "conversation.write", params: MidParams,
    body: z.object({ summary: z.string().max(20000).optional(), decisions: z.array(z.string().max(2000)).max(100).optional() }), response: Meeting,
  }),
  meetingInterject: r({
    phase: 5, method: "POST", path: "/meetings/:mid/interject", summary: "人插话", tags: ["meeting"], auth: "user", permission: "conversation.write", params: MidParams,
    body: z.object({ text: z.string().min(1) }), response: Message,
  }),

  // ───────── P5：管理（节点 / 审计） ─────────
  adminNodes: r({ phase: 5, method: "GET", path: "/admin/nodes", summary: "节点列表", tags: ["admin"], auth: "user", permission: "platform.admin", response: z.object({ items: z.array(NodeInfo) }) }),
  adminJoinToken: r({
    phase: 5, method: "POST", path: "/admin/nodes/join-token", summary: "发节点加入令牌", tags: ["admin"], auth: "user", permission: "platform.admin",
    body: z.object({ ttlSec: z.number().int().min(60).max(86400).default(3600) }), response: z.object({ token: z.string(), expiresAt: Timestamp, gateway: z.string() }),
  }),
  adminNodeRevoke: r({
    phase: 5, method: "DELETE", path: "/admin/nodes/:nid", summary: "吊销节点（reason 必填：query 或 x-agentsaas-reason 头）", tags: ["admin"], auth: "user", permission: "platform.admin",
    params: z.object({ nid: NodeId }), query: z.object({ reason: z.string().min(1).optional() }), response: Ok,
  }),
  adminNodeDrain: r({
    phase: 5, method: "POST", path: "/admin/nodes/:nid/drain", summary: "排空 / 取消排空节点（不再接收新放置）", tags: ["admin"], auth: "user", permission: "platform.admin",
    params: z.object({ nid: NodeId }), body: z.object({ on: z.boolean(), reason: z.string().min(1) }), response: Ok,
  }),
  adminTenantRuntime: r({
    phase: 5, method: "PUT", path: "/admin/tenants/:tid/runtime", summary: "租户容器运行时要求（runsc = gVisor；null = 取消）", tags: ["admin"], auth: "user", permission: "platform.admin",
    params: TidParams, body: z.object({ runtime: z.enum(["runc", "runsc"]).nullable(), reason: z.string().min(1) }),
    response: z.object({ tenantId: TenantId, runtime: z.enum(["runc", "runsc"]).nullable() }),
  }),
  adminPlacements: r({ phase: 5, method: "GET", path: "/admin/placements", summary: "agent 放置", tags: ["admin"], auth: "user", permission: "platform.admin", query: z.object({ nodeId: NodeId.optional() }), response: z.object({ items: z.array(AgentPlacement) }) }),
  adminMigrate: r({
    phase: 5, method: "POST", path: "/admin/agents/:aid/migrate", summary: "迁移 agent 到其他节点", tags: ["admin"], auth: "user", permission: "platform.admin",
    params: z.object({ aid: AgentId }), body: z.object({ targetNodeId: NodeId, reason: z.string().min(1) }), response: AgentPlacement, errors: ["agent.busy", "node.none_available"],
  }),
  adminPin: r({
    phase: 5, method: "POST", path: "/admin/agents/:aid/pin", summary: "钉住 / 取消钉住", tags: ["admin"], auth: "user", permission: "platform.admin",
    params: z.object({ aid: AgentId }), body: z.object({ nodeId: NodeId.nullable(), reason: z.string().min(1) }), response: AgentPlacement,
  }),
  adminAudit: r({
    phase: 5, method: "GET", path: "/admin/audit", summary: "审计日志", tags: ["admin"], auth: "user", permission: "platform.admin",
    query: PageQuery.extend({ actor: z.string().optional(), action: z.string().optional(), tenantId: TenantId.optional() }),
    response: page(z.object({ id: z.string(), at: Timestamp, actor: z.string(), action: z.string(), target: z.string(), tenantId: TenantId.nullable(), reason: z.string().nullable(), data: z.unknown() })),
  }),

  // ───────── P6：计费 ─────────
  usageQuery: r({
    phase: 6, method: "GET", path: "/tenants/:tid/usage", summary: "用量统计", tags: ["billing"], auth: "user", permission: "tenant.read", params: TidParams,
    query: z.object({ from: Timestamp, to: Timestamp, groupBy: z.enum(["agent", "project", "kind", "day"]).default("day"), kind: UsageKind.optional() }),
    response: z.object({ rows: z.array(z.object({ key: z.string(), costMicroUsd: z.number().int(), amount: z.number(), unit: z.string() })), totalMicroUsd: z.number().int() }),
  }),
  usageRecords: r({ phase: 6, method: "GET", path: "/tenants/:tid/usage/records", summary: "用量明细", tags: ["billing"], auth: "user", permission: "tenant.read", params: TidParams, query: PageQuery, response: page(UsageRecord) }),
  budgetPut: r({
    phase: 6, method: "PUT", path: "/tenants/:tid/budgets", summary: "租户预算", tags: ["billing"], auth: "user", permission: "billing.manage", params: TidParams,
    body: z.object({ monthlyLimitMicroUsd: z.number().int().nonnegative().nullable(), perAgentDefaultMicroUsd: z.number().int().nonnegative().nullable(), alertAtPercent: z.number().int().min(1).max(100).default(80) }),
    response: TenantBudget,
  }),
  budgetGet: r({ phase: 6, method: "GET", path: "/tenants/:tid/budgets", summary: "读取租户预算（未设置时为默认值）", tags: ["billing"], auth: "user", permission: "tenant.read", params: TidParams, response: TenantBudget }),
  paymentGet: r({
    phase: 6, method: "GET", path: "/payments/:payid", summary: "充值单状态（扫码 / 跳转支付后轮询）", tags: ["billing"], auth: "user", permission: "tenant.read",
    params: z.object({ payid: z.string() }), response: Payment,
  }),
  walletGet: r({ phase: 6, method: "GET", path: "/tenants/:tid/wallet", summary: "钱包余额", tags: ["billing"], auth: "user", permission: "tenant.read", params: TidParams, response: Wallet }),
  walletTopup: r({
    phase: 6, method: "POST", path: "/tenants/:tid/wallet/topup", summary: "充值（返回支付跳转）；amount 为币种最小单位（USD 美分 / CNY 分）", tags: ["billing"], auth: "user", idempotent: true, permission: "billing.manage", params: TidParams,
    body: z.object({ amount: z.number().int().positive(), currency: Currency, provider: z.enum(["stripe", "alipay", "wechat", "fake"]) }),
    response: z.object({ paymentId: z.string(), redirectUrl: z.url().nullable(), qrCode: z.string().nullable() }), errors: ["billing.provider_unavailable"],
  }),
  invoiceList: r({
    phase: 6, method: "GET", path: "/tenants/:tid/invoices", summary: "账单", tags: ["billing"], auth: "user", permission: "tenant.read", params: TidParams, query: PageQuery,
    response: page(z.object({ id: z.string(), period: z.string(), totalMicroUsd: z.number().int(), currency: Currency, status: z.enum(["open", "paid", "void"]), createdAt: Timestamp })),
  }),

  // ───────── P6：市场 ─────────
  listingSearch: r({
    phase: 6, method: "GET", path: "/market/listings", summary: "市场搜索", tags: ["market"], auth: "user",
    query: PageQuery.extend({ role: z.string().optional(), q: z.string().optional(), sort: z.enum(["popular", "rating", "newest"]).default("popular") }), response: page(Listing),
  }),
  listingGet: r({ phase: 6, method: "GET", path: "/market/listings/:lid", summary: "商品详情", tags: ["market"], auth: "user", params: z.object({ lid: ListingId }), response: Listing.extend({ template: Template }) }),
  listingCreate: r({
    phase: 6, method: "POST", path: "/tenants/:tid/listings", summary: "上架模板", tags: ["market"], auth: "user", idempotent: true, permission: "market.publish", params: TidParams,
    body: Listing.pick({ templateId: true, title: true, description: true, pricing: true }), response: Listing,
  }),
  listingPatch: r({
    phase: 6, method: "PATCH", path: "/tenants/:tid/listings/:lid", summary: "编辑商品（按模板当前内容重新快照，重新进入待审核；已上架的旧版本审核期间照常出租）", tags: ["market"], auth: "user",
    permission: "market.publish", params: TidParams.extend({ lid: ListingId }),
    body: Listing.pick({ title: true, description: true, pricing: true }).partial(), response: Listing,
  }),
  listingReviews: r({
    phase: 6, method: "GET", path: "/market/listings/:lid/reviews", summary: "评价列表（最新在前）", tags: ["market"], auth: "user",
    params: z.object({ lid: ListingId }), query: PageQuery, response: page(ListingReview),
  }),
  listingMine: r({ phase: 6, method: "GET", path: "/tenants/:tid/listings", summary: "我上架的", tags: ["market"], auth: "user", permission: "tenant.read", params: TidParams, response: z.object({ items: z.array(Listing) }) }),
  listingRent: r({
    // 权限（agent.control）按 body.projectId 在 handler 里校验：路由层不能按商品定位租户（商品属于作者租户）
    phase: 6, method: "POST", path: "/market/listings/:lid/rent", summary: "租用（在自己的项目里实例化 agent；需要对 body.projectId 有 agent.control）", tags: ["market"], auth: "user", idempotent: true,
    params: z.object({ lid: ListingId }),
    body: z.object({ projectId: ProjectId, credentialId: z.string(), name: z.string(), days: z.number().int().min(1).max(365).optional() }),
    response: Rental, errors: ["billing.insufficient_balance", "credential.not_allowed"],
  }),
  listingReview: r({
    phase: 6, method: "POST", path: "/market/listings/:lid/reviews", summary: "评价", tags: ["market"], auth: "user", params: z.object({ lid: ListingId }),
    body: z.object({ rating: z.number().int().min(1).max(5), comment: z.string().max(2000).default("") }), response: Ok,
  }),
  adminListingList: r({
    phase: 6, method: "GET", path: "/admin/listings", summary: "审核队列（缺省待审核）", tags: ["admin"], auth: "user", permission: "platform.admin",
    query: PageQuery.extend({ status: z.enum(["draft", "pending_review", "published", "suspended"]).default("pending_review") }), response: page(Listing),
  }),
  adminListingModerate: r({
    phase: 6, method: "POST", path: "/admin/listings/:lid/moderate", summary: "审核上架", tags: ["admin"], auth: "user", permission: "platform.admin",
    params: z.object({ lid: ListingId }), body: z.object({ decision: z.enum(["publish", "reject", "suspend"]), reason: z.string() }), response: Listing,
  }),
  rentalList: r({ phase: 6, method: "GET", path: "/tenants/:tid/rentals", summary: "我的租用", tags: ["market"], auth: "user", permission: "tenant.read", params: TidParams, response: z.object({ items: z.array(Rental) }) }),
} as const satisfies Record<string, RouteDef>;
